import React from 'react';
import { MapContainer, TileLayer, Marker, Popup, CircleMarker } from 'react-leaflet';
import L from 'leaflet';
import { ListingWithPathImages, Poi } from '@/types';
import MarkerClusterGroup from 'react-leaflet-cluster';

// --- Leaflet CSS ---
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';

// --- Leaflet Icon Fix ---
import iconRetinaUrl from 'leaflet/dist/images/marker-icon-2x.png';
import iconUrl from 'leaflet/dist/images/marker-icon.png';
import shadowUrl from 'leaflet/dist/images/marker-shadow.png';

L.Icon.Default.mergeOptions({
  iconRetinaUrl,
  iconUrl,
  shadowUrl,
});

interface ListingMapboxProps {
  listings: Record<string, ListingWithPathImages[]>;
  schoolLatLong: { lat: number; lng: number };
  pois: Poi[];
  // Changes whenever a new search completes; used to force the marker cluster
  // layer to rebuild with the new results without remounting the whole map.
  refreshKey?: number | string;
}

interface MarkerData {
  position: [number, number];
  listings?: ListingWithPathImages[];
  poi?: Poi;
  type: 'listing' | 'poi';
}

export function ListingMapbox({ listings, schoolLatLong, pois, refreshKey }: ListingMapboxProps) {
  // Convert listings to marker data
  const markers: MarkerData[] = Object.entries(listings).map(([key, listingsAtLocation]) => {
    const [lng, lat] = key.split(', ').map(Number);
    return {
      position: [lat, lng],
      listings: listingsAtLocation,
      type: 'listing',
    };
  });

  // Add POIs (points of interest always displayed)
  if (pois.length > 0) {
    pois.forEach((poi) => {
      markers.push({
        position: [poi.latitude, poi.longitude],
        poi,
        type: 'poi',
      });
    });
  }

  // Mapbox configuration
  const mapboxStyleId = 'mapbox/streets-v12';
  const mapboxTileUrl = `https://api.mapbox.com/styles/v1/${mapboxStyleId}/tiles/{z}/{x}/{y}@2x?access_token=${window.ENV.MAPBOX_ACCESS_TOKEN}`;
  const mapboxAttribution = `
    &copy; <a href="https://www.mapbox.com/about/maps/" target="_blank">Mapbox</a>
    &copy; <a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a> contributors
    <a href="https://www.mapbox.com/map-feedback/" target="_blank">Improve this map</a>`;

  const renderListingPopup = (listings: ListingWithPathImages[]) => (
    <Popup maxWidth={300}>
      {listings.map((listing, index) => (
        <div key={index} className="card m-2" style={{ maxWidth: '200px' }}>
          {listing.images[0] && (
            <a href={listing.listing_path} target="_blank" rel="noopener noreferrer">
              <img
                src={listing.images[0].url}
                className="card-img-top"
                alt={listing.images[0].alt || 'Listing photo'}
                style={{ width: '100%', aspectRatio: '4/3', objectFit: 'cover' }}
              />
            </a>
          )}
          <div className="card-body">
            <div className="card-title fw-bold">
              <a href={listing.listing_path} target="_blank" rel="noopener noreferrer">
                {listing.title || ''}
              </a>
            </div>
            <div className="card-text">
              ${listing.price} / {listing.beds} beds
            </div>
          </div>
        </div>
      ))}
    </Popup>
  );

  const renderPoiPopup = (poi: Poi) => (
    <Popup>
      <div>
        <h5>{poi.title || 'Point of Interest'}</h5>
        {poi.description?.split('\\n').map((line, index) => (
          <p key={index} style={{ margin: '0.25rem 0' }}>
            {line}
          </p>
        ))}
      </div>
    </Popup>
  );

  const createClusterCustomIcon = (cluster: any) => {
    const count = cluster.getChildCount();
    let className = 'marker-cluster ';

    if (count < 10) className += 'marker-cluster-small';
    else if (count < 30) className += 'marker-cluster-medium';
    else className += 'marker-cluster-large';

    return L.divIcon({
      html: `<div><span>${count}</span></div>`,
      className: className,
      iconSize: L.point(40, 40),
    });
  };

  return (
    <MapContainer
      center={[schoolLatLong.lat, schoolLatLong.lng]}
      zoom={13}
      style={{ height: '600px', width: '100%' }}
      scrollWheelZoom={true}
    >
      <TileLayer attribution={mapboxAttribution} url={mapboxTileUrl} tileSize={512} zoomOffset={-1} />

      <MarkerClusterGroup key={refreshKey} chunkedLoading iconCreateFunction={createClusterCustomIcon}>
        {markers.map((marker, index) =>
          marker.type === 'listing' ? (
            <Marker key={`listing-${index}`} position={marker.position}>
              {marker.listings && renderListingPopup(marker.listings)}
            </Marker>
          ) : (
            <CircleMarker
              key={`poi-${index}`}
              center={marker.position}
              radius={8}
              pathOptions={{
                color: '#f28cb1',
                fillColor: '#f28cb1',
                fillOpacity: 1,
              }}
            >
              {marker.poi && renderPoiPopup(marker.poi)}
            </CircleMarker>
          )
        )}
      </MarkerClusterGroup>
    </MapContainer>
  );
}
